import { queryOptions } from '@tanstack/react-query';
import { unwrap, unwrapPage } from '@kidova/api-client';
import { api, setCsrf } from './api';

type Range = '7d' | '30d' | '90d';
// Lists the dashboard shows in full; §10.3 pages at most 100 items per call.
const ALL = 100;

export const keys = {
  session: ['session'] as const,
  home: ['home'] as const,
  activity: (range: Range) => ['activity', range] as const,
  family: ['family'] as const,
  profiles: ['profiles'] as const,
  profile: (id: string) => ['profile', id] as const,
  rules: (id: string) => ['profile', id, 'rules'] as const,
  schedules: (id: string) => ['profile', id, 'schedules'] as const,
  appLimits: (id: string) => ['profile', id, 'app-limits'] as const,
  video: (id: string) => ['profile', id, 'video'] as const,
  profileDevices: (id: string) => ['profile', id, 'devices'] as const,
  report: (id: string, range: Range) => ['profile', id, 'report', range] as const,
  devices: ['devices'] as const,
  requests: (status: string) => ['requests', status] as const,
  blockReports: ['block-reports'] as const,
  billing: ['billing'] as const,
  plans: (country: string) => ['plans', country] as const,
  invoices: ['invoices'] as const,
  privacy: ['privacy'] as const,
  members: ['members'] as const,
  invites: ['invites'] as const,
  sessions: ['sessions'] as const,
  passkeys: ['passkeys'] as const,
};

export const q = {
  session: () =>
    queryOptions({
      queryKey: keys.session,
      queryFn: async () => {
        const session = await unwrap(api.GET('/v1/web/session'));
        setCsrf(session.csrf_token);
        return session;
      },
      staleTime: 5 * 60_000,
    }),
  home: () =>
    queryOptions({ queryKey: keys.home, queryFn: () => unwrap(api.GET('/v1/family/home')) }),
  activity: (range: Range = '7d') =>
    queryOptions({
      queryKey: keys.activity(range),
      queryFn: () => unwrap(api.GET('/v1/family/activity', { params: { query: { range } } })),
    }),
  family: () =>
    queryOptions({ queryKey: keys.family, queryFn: () => unwrap(api.GET('/v1/family')) }),
  profiles: () =>
    queryOptions({
      queryKey: keys.profiles,
      queryFn: async () =>
        (await unwrapPage(api.GET('/v1/profiles', { params: { query: { limit: ALL } } }))).items,
    }),
  profile: (id: string) =>
    queryOptions({
      queryKey: keys.profile(id),
      queryFn: () =>
        unwrap(api.GET('/v1/profiles/{profile_id}', { params: { path: { profile_id: id } } })),
    }),
  rules: (id: string) =>
    queryOptions({
      queryKey: keys.rules(id),
      queryFn: async () =>
        (
          await unwrapPage(
            api.GET('/v1/profiles/{profile_id}/rules', {
              params: { path: { profile_id: id }, query: { limit: ALL } },
            }),
          )
        ).items,
    }),
  schedules: (id: string) =>
    queryOptions({
      queryKey: keys.schedules(id),
      queryFn: async () =>
        (
          await unwrapPage(
            api.GET('/v1/profiles/{profile_id}/schedules', {
              params: { path: { profile_id: id }, query: { limit: ALL } },
            }),
          )
        ).items,
    }),
  appLimits: (id: string) =>
    queryOptions({
      queryKey: keys.appLimits(id),
      queryFn: () =>
        unwrap(
          api.GET('/v1/profiles/{profile_id}/app-limits', { params: { path: { profile_id: id } } }),
        ),
    }),
  video: (id: string) =>
    queryOptions({
      queryKey: keys.video(id),
      queryFn: async () =>
        (
          await unwrapPage(
            api.GET('/v1/profiles/{profile_id}/video-allowlist', {
              params: { path: { profile_id: id }, query: { limit: ALL } },
            }),
          )
        ).items,
    }),
  profileDevices: (id: string) =>
    queryOptions({
      queryKey: keys.profileDevices(id),
      queryFn: async () =>
        (
          await unwrapPage(
            api.GET('/v1/profiles/{profile_id}/devices', {
              params: { path: { profile_id: id }, query: { limit: ALL } },
            }),
          )
        ).items,
    }),
  report: (id: string, range: Range) =>
    queryOptions({
      queryKey: keys.report(id, range),
      queryFn: () =>
        unwrap(
          api.GET('/v1/profiles/{profile_id}/reports', {
            params: { path: { profile_id: id }, query: { range } },
          }),
        ),
    }),
  devices: () =>
    queryOptions({
      queryKey: keys.devices,
      queryFn: async () =>
        (await unwrapPage(api.GET('/v1/devices', { params: { query: { limit: ALL } } }))).items,
    }),
  requests: (status: 'pending' | 'approved' | 'denied' | 'expired' | 'all') =>
    queryOptions({
      queryKey: keys.requests(status),
      queryFn: async () =>
        (
          await unwrapPage(
            api.GET('/v1/access-requests', {
              params: { query: { limit: ALL, ...(status === 'all' ? {} : { status }) } },
            }),
          )
        ).items,
    }),
  blockReports: () =>
    queryOptions({
      queryKey: keys.blockReports,
      queryFn: async () =>
        (await unwrapPage(api.GET('/v1/block-reports', { params: { query: { limit: ALL } } })))
          .items,
    }),
  billing: () =>
    queryOptions({ queryKey: keys.billing, queryFn: () => unwrap(api.GET('/v1/billing')) }),
  plans: (country: string) =>
    queryOptions({
      queryKey: keys.plans(country),
      queryFn: () => unwrap(api.GET('/v1/plans', { params: { query: { country } } })),
      staleTime: 60 * 60_000,
    }),
  invoices: () =>
    queryOptions({
      queryKey: keys.invoices,
      queryFn: async () =>
        (await unwrapPage(api.GET('/v1/billing/invoices', { params: { query: { limit: ALL } } })))
          .items,
    }),
  privacy: () =>
    queryOptions({ queryKey: keys.privacy, queryFn: () => unwrap(api.GET('/v1/privacy')) }),
  members: () =>
    queryOptions({
      queryKey: keys.members,
      queryFn: async () =>
        (await unwrapPage(api.GET('/v1/family/members', { params: { query: { limit: ALL } } })))
          .items,
    }),
  invites: () =>
    queryOptions({
      queryKey: keys.invites,
      queryFn: async () =>
        (await unwrapPage(api.GET('/v1/family/invites', { params: { query: { limit: ALL } } })))
          .items,
    }),
  sessions: () =>
    queryOptions({ queryKey: keys.sessions, queryFn: () => unwrap(api.GET('/v1/auth/sessions')) }),
  passkeys: () =>
    queryOptions({ queryKey: keys.passkeys, queryFn: () => unwrap(api.GET('/v1/auth/passkeys')) }),
};
